// NOTE: This array is duplicated in the backend at
// apps/api/prisma/starter-checklist-templates.ts (used to seed the database) —
// keep the two in sync.

export type StarterItemType = 'YesNo' | 'Text' | 'TextArea' | 'Number'

export type StarterItem = {
  label: string
  type: StarterItemType
  isRequired: boolean
}

export type StarterTemplate = {
  slug: string
  name: string
  description: string
  items: StarterItem[]
}

export const STARTER_CHECKLIST_TEMPLATES: StarterTemplate[] = [
  {
    slug: 'pre-departure-cabin',
    name: 'Pre-departure cabin',
    description: 'Cabin preparation before passenger boarding.',
    items: [
      { label: 'Ensure all on-charge items are polished & stowed away:\n• iPads\n• Head sets\n• Power banks\n• Vacuum\n• Vacuum charger stowed away neatly\n• Charging cables folded away neatly', type: 'YesNo', isRequired: true },
      { label: 'Prepare aft lavatory:\n• Shade in sheer position\n• Tissues folded & stamped with ‘P’\n• Hand & face towels perfectly folded and aligned', type: 'YesNo', isRequired: true },
      { label: 'Prepare bed setting:\n• Eye shades\n• Head sets\n• Night amenity bag (La Mer)\n• Chargers', type: 'YesNo', isRequired: true },
      { label: 'Stow away bed & mattress bags', type: 'YesNo', isRequired: true },
      { label: 'Prepare welcome table:\n• 2 silver trays (big & small) with tray linen\n• Flower arrangement\n• Fruit & snack display\n• Water tray\n• Hand towels tray (hot and cold)\n• Extra drinks tray (occasional)\n• Drinks menu / food menu', type: 'YesNo', isRequired: true },
      { label: 'Polish, dust & vacuum all cabin surfaces & aft lavatory', type: 'YesNo', isRequired: true },
      { label: 'If time permits, put laundry away', type: 'YesNo', isRequired: true },
    ],
  },
  {
    slug: 'pre-departure-galley',
    name: 'Pre-departure galley',
    description: 'Galley and systems preparation before boarding.',
    items: [
      { label: 'Do cabin and galley system setup:\n• Fridge on\n• Apple TV connected\n• Welcome music by occasion\n• Maps view connected\n• Shades open\n• Lights adjusted to day or night\n• Day flight: leave lights by default\n• Night flight: soft dim lights', type: 'YesNo', isRequired: true },
      { label: 'Confirm water tank is filled and full\n• Lav has been serviced\n• Load ice\n• Ensure all items received by FBO (laundry, fridge bag, chocolate bag, catering, extras)', type: 'YesNo', isRequired: true },
      { label: 'Fill Nespresso and kettle with bottled water', type: 'YesNo', isRequired: true },
      { label: 'Confirm, prepare & arrange catering neatly', type: 'YesNo', isRequired: true },
      { label: 'Prepare hot and cold towels & stow away', type: 'YesNo', isRequired: true },
      { label: 'Prepare fwd lavatory:\n• Shade in sheer position\n• Tissues and toilet paper folded neatly\n• Hand and face towels perfectly folded and aligned', type: 'YesNo', isRequired: true },
      { label: 'Polish, dust, vacuum, wipe:\n• Entrance (stairs, hand railings, side panels, entrance carpet)\n• Galley & floor\n• Ensure galley is neat and of high cleaning standards', type: 'YesNo', isRequired: true },
      { label: 'Open crew rest curtain for boarding', type: 'YesNo', isRequired: true },
    ],
  },
  {
    slug: 'post-flight-cabin',
    name: 'Post-flight cabin',
    description: 'Cabin turnaround and cleaning after landing.',
    items: [
      { label: 'Offload hand luggage & items left by pax:\n• Large bags via cargo door\n• Small bags via main fwd door', type: 'YesNo', isRequired: true },
      { label: 'Check cabinets for forgotten items and report ASAP to pilot if items are found', type: 'YesNo', isRequired: true },
      { label: 'Remove used mattress and bedding from seats, put used linen in laundry bags, stow bedding bags in cargo accordingly', type: 'YesNo', isRequired: true },
      { label: 'Charge all items used:\n• iPads (check each iPad in cabin to ensure it is not used)\n• Head sets\n• Power banks', type: 'YesNo', isRequired: true },
      { label: 'Re-prepare all chargers used, ensure all is neat:\n• Wifi cards at each seat and damaged ones removed and replaced\n• Check all compartments for trash or items taken off board that need to be replaced\n• Clean and replace cabin bin bag\n• Arrange all cabin compartments neatly', type: 'YesNo', isRequired: true },
      { label: 'Clean aft lavatory:\n• Remove used hand and face towels to laundry bag and replace with fresh towels\n• Refill tissues and toilet paper roll\n• Replace toiletries used\n• Clean and replace lavatory bin bag\n• Sanitize toilet, polish and dust lavatory', type: 'YesNo', isRequired: true },
      { label: 'Clean vacuum cleaner after use\n• Charge vacuum cleaner in cabin', type: 'YesNo', isRequired: true },
      { label: 'Uplift all items used', type: 'YesNo', isRequired: true },
    ],
  },
  {
    slug: 'post-flight-galley',
    name: 'Post-flight galley',
    description: 'Galley cleaning and offload after landing.',
    items: [
      { label: 'Empty areas:\n• Fridge\n• Oven\n• Microwave\n• Closet with catering items\n• Drawers with catering items', type: 'YesNo', isRequired: true },
      { label: 'Clean:\n• Fridge\n• Oven / microwave\n• Nespresso machine (empty water and wash it properly)\n• Kettle (empty water and wash)\n• Sink\n• Drawers\n• Closets', type: 'YesNo', isRequired: true },
      { label: 'Offload garbage bag and replace with new bag', type: 'YesNo', isRequired: true },
      { label: 'Clean fwd lavatory:\n• Remove used hand and face towels to laundry bags and replace with fresh towels\n• Refill tissues and toilet paper roll\n• Replace toiletries used\n• Clean and replace lavatory bin bag\n• Sanitize toilet, polish and dust lavatory', type: 'YesNo', isRequired: true },
      { label: 'Clean, polish, dust and vacuum galley area', type: 'YesNo', isRequired: true },
      { label: 'Put all shades in sheer position\n• Offload laundry\n• Offload fridge bags\n• Offload chocolate bag\n• Take picture of all items given to FBO', type: 'YesNo', isRequired: true },
      { label: 'Report stains, damages to captain and engineer', type: 'YesNo', isRequired: true },
    ],
  },
  {
    slug: 'flight-report',
    name: 'Flight report',
    description: 'Inflight service debrief and cabin status report.',
    items: [
      { label: 'General flight information:\n• Crew names\n• Flight route', type: 'TextArea', isRequired: false },
      { label: 'Did they eat and what did they eat', type: 'TextArea', isRequired: false },
      { label: 'Were drinks served and what', type: 'TextArea', isRequired: false },
      { label: 'Did they sleep and where', type: 'TextArea', isRequired: false },
      { label: 'Mood inflight', type: 'TextArea', isRequired: false },
      { label: 'Requests made', type: 'TextArea', isRequired: false },
      { label: 'PAX profile update', type: 'TextArea', isRequired: false },
      { label: 'Lav sanitized', type: 'YesNo', isRequired: true },
      { label: 'Fridge and oven / microwave cleaned', type: 'YesNo', isRequired: true },
      { label: 'Kettle and Nespresso cleaned', type: 'YesNo', isRequired: true },
      { label: 'Galley floor wiped / galley / cabin / lav polished', type: 'YesNo', isRequired: true },
      { label: 'Water placed in cabin cup holders', type: 'YesNo', isRequired: true },
      { label: 'Beds unmade or prepared for next flight', type: 'YesNo', isRequired: true },
      { label: 'Trash removed / galley / cabin / lav', type: 'YesNo', isRequired: true },
      { label: 'New bin bags placed / galley / cabin / lav', type: 'YesNo', isRequired: true },
      { label: 'Cabin / galley / crew rest vacuumed', type: 'YesNo', isRequired: true },
      { label: 'Dyson charging', type: 'YesNo', isRequired: true },
      { label: 'Headsets charging', type: 'YesNo', isRequired: true },
      { label: 'iPads charging', type: 'YesNo', isRequired: true },
      { label: 'Power banks charging', type: 'YesNo', isRequired: true },
      { label: 'Jet bed pump charging', type: 'YesNo', isRequired: true },
      { label: 'Water / beverages offloaded (winter ops)', type: 'YesNo', isRequired: true },
      { label: 'Water tank purged (winter ops)', type: 'YesNo', isRequired: true },
      { label: 'Chocolate bag location', type: 'Text', isRequired: false },
      { label: 'Dirty laundry location', type: 'Text', isRequired: false },
      { label: 'Dishes sent for washing location', type: 'Text', isRequired: false },
      { label: 'Fridge bags location', type: 'Text', isRequired: false },
      { label: 'New snags / interior scratches / damage', type: 'TextArea', isRequired: false },
      { label: 'Snags reported to pilot or engineer', type: 'YesNo', isRequired: true },
      { label: 'Items missing', type: 'TextArea', isRequired: false },
      { label: 'Items to reorder', type: 'TextArea', isRequired: false },
      { label: 'New items placed on board', type: 'TextArea', isRequired: false },
      { label: 'Remarks', type: 'TextArea', isRequired: false },
    ],
  },
  {
    slug: 'handover',
    name: 'Handover',
    description: 'Crew handover notes for the next flight.',
    items: [
      { label: 'Name of crew', type: 'Text', isRequired: false },
      { label: 'Laundry and location', type: 'TextArea', isRequired: false },
      { label: 'Uplift / replenish', type: 'TextArea', isRequired: false },
      { label: 'Items needed to buy or order', type: 'TextArea', isRequired: false },
      { label: 'Items available for next flight', type: 'TextArea', isRequired: false },
      { label: 'Water count', type: 'Text', isRequired: false },
      { label: 'Chocolate bag location', type: 'Text', isRequired: false },
      { label: 'Chocolate bag expiry dates checked', type: 'Text', isRequired: false },
      { label: 'Room temp bag location', type: 'Text', isRequired: false },
      { label: 'Extra bags location', type: 'Text', isRequired: false },
      { label: 'Galley expiry dates checked', type: 'YesNo', isRequired: true },
      { label: 'iPads charged or need charging', type: 'Text', isRequired: false },
      { label: 'Headsets charged or needs charging', type: 'Text', isRequired: false },
      { label: 'Dyson charged or needs charging', type: 'Text', isRequired: false },
      { label: 'Jet bed pumps charged or needs charging', type: 'Text', isRequired: false },
      { label: 'Cabin and galley cleaned or needs cleaning', type: 'Text', isRequired: false },
      { label: 'Remarks', type: 'TextArea', isRequired: false },
    ],
  },
]
